import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { CustomerSelectWithCreate } from "@/components/controller/CustomerSelectWithCreate";
import { useUpdateProject } from "@/hooks/useProjects";
import { toast } from "sonner";

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: any;
}

export const EditProjectDialog = ({ open, onOpenChange, project }: Props) => {
  const updateProject = useUpdateProject();
  const [form, setForm] = useState<Record<string, any>>({});

  useEffect(() => {
    if (open && project) {
      setForm({
        name: project.name ?? "",
        customer_id: project.customer_id ?? "",
        description: project.description ?? "",
        status: project.status ?? "active",
        address: project.address ?? "",
        city: project.city ?? "",
        state: project.state ?? "",
        zip: project.zip ?? "",
        customer_po_number: project.customer_po_number ?? "",
        has_unit_schedule: project.has_unit_schedule ?? false,
        billing_cycle_type: project.billing_cycle_type ?? "monthly",
        billing_cutoff_day: String(project.billing_cutoff_day ?? "25"),
        billing_cutoff_day2: String(project.billing_cutoff_day2 ?? "15"),
        billing_type: project.billing_type ?? "unit_based",
        is_public_works: project.is_public_works ?? false,
      });
    }
  }, [open, project]);

  const set = (key: string, val: any) => setForm((p) => ({ ...p, [key]: val }));

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error("Project name is required");
      return;
    }
    try {
      await updateProject.mutateAsync({
        id: project.id,
        name: form.name.trim(),
        customer_id: form.customer_id || null,
        description: form.description?.trim() || null,
        status: form.status,
        address: form.address?.trim() || null,
        city: form.city?.trim() || null,
        state: form.state?.trim() || null,
        zip: form.zip?.trim() || null,
        customer_po_number: form.customer_po_number?.trim() || null,
        has_unit_schedule: form.has_unit_schedule,
        billing_cycle_type: form.has_unit_schedule ? form.billing_cycle_type : null,
        billing_cutoff_day: form.has_unit_schedule ? parseInt(form.billing_cutoff_day) || null : null,
        billing_cutoff_day2: form.has_unit_schedule && form.billing_cycle_type === "bimonthly"
          ? parseInt(form.billing_cutoff_day2) || null : null,
        billing_type: form.billing_type,
        is_public_works: form.is_public_works,
      });
      toast.success("Project updated");
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || "Failed to update project");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Project</SheetTitle>
        </SheetHeader>
        <div className="space-y-6 py-4">
          {/* Core Info */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Project Info</h3>
            <div className="space-y-2">
              <Label>Project Name *</Label>
              <Input value={form.name ?? ""} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Customer</Label>
              <CustomerSelectWithCreate
                value={form.customer_id ?? ""}
                onValueChange={(v) => set("customer_id", v)}
                placeholder="Select customer"
              />
            </div>
            <div className="space-y-2">
              <Label>Customer PO / Ref #</Label>
              <Input value={form.customer_po_number ?? ""} onChange={(e) => set("customer_po_number", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status ?? "active"} onValueChange={(v) => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.description ?? ""} onChange={(e) => set("description", e.target.value)} rows={2} />
            </div>
          </div>

          <Separator />

          {/* Location */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Job Location</h3>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={form.address ?? ""} onChange={(e) => set("address", e.target.value)} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>City</Label><Input value={form.city ?? ""} onChange={(e) => set("city", e.target.value)} /></div>
              <div><Label>State</Label><Input value={form.state ?? ""} onChange={(e) => set("state", e.target.value)} maxLength={2} /></div>
              <div><Label>Zip</Label><Input value={form.zip ?? ""} onChange={(e) => set("zip", e.target.value)} maxLength={10} /></div>
            </div>
          </div>

          <Separator />

          {/* Billing Type */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Project Billing Type</h3>
            <RadioGroup value={form.billing_type ?? "unit_based"} onValueChange={(v) => {
              set("billing_type", v);
              if (v === "unit_based") set("has_unit_schedule", true);
              else if (v === "sov_only") set("has_unit_schedule", false);
            }} className="space-y-2">
              <Label className={cn("flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors", form.billing_type === "unit_based" ? "border-primary bg-primary/5" : "hover:border-primary/50")}>
                <RadioGroupItem value="unit_based" className="mt-0.5" />
                <div>
                  <span className="text-sm font-medium">Unit-based</span>
                  <p className="text-xs text-muted-foreground">Residential, multifamily, hotels</p>
                </div>
              </Label>
              <Label className={cn("flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors", form.billing_type === "sov_only" ? "border-primary bg-primary/5" : "hover:border-primary/50")}>
                <RadioGroupItem value="sov_only" className="mt-0.5" />
                <div>
                  <span className="text-sm font-medium">SOV only</span>
                  <p className="text-xs text-muted-foreground">Commercial, lump sum, service</p>
                </div>
              </Label>
              <Label className={cn("flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors", form.billing_type === "mixed" ? "border-primary bg-primary/5" : "hover:border-primary/50")}>
                <RadioGroupItem value="mixed" className="mt-0.5" />
                <div>
                  <span className="text-sm font-medium">Mixed</span>
                  <p className="text-xs text-muted-foreground">Combination of unit + SOV tracking</p>
                </div>
              </Label>
            </RadioGroup>
          </div>

          <Separator />

          {/* Unit Schedule / Billing */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Unit Schedule</h3>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox
                checked={form.has_unit_schedule ?? false}
                onCheckedChange={(v) => set("has_unit_schedule", !!v)}
              />
              This project has a unit schedule
            </label>

            {form.has_unit_schedule && (
              <div className="space-y-3 pl-6 border-l-2 border-primary/20">
                <div className="space-y-2">
                  <Label>Billing Cycle</Label>
                  <Select value={form.billing_cycle_type ?? "monthly"} onValueChange={(v) => set("billing_cycle_type", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="bimonthly">Bi-Monthly</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {form.billing_cycle_type === "monthly" && (
                  <div className="space-y-2">
                    <Label>Cutoff day of each month</Label>
                    <Input
                      type="number" min={1} max={31}
                      value={form.billing_cutoff_day ?? "25"}
                      onChange={(e) => set("billing_cutoff_day", e.target.value)}
                      className="w-24"
                    />
                  </div>
                )}

                {form.billing_cycle_type === "bimonthly" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>First cutoff day</Label>
                      <Input type="number" min={1} max={31} value={form.billing_cutoff_day ?? "25"} onChange={(e) => set("billing_cutoff_day", e.target.value)} className="w-24" />
                    </div>
                    <div className="space-y-2">
                      <Label>Second cutoff day</Label>
                      <Input type="number" min={1} max={31} value={form.billing_cutoff_day2 ?? "15"} onChange={(e) => set("billing_cutoff_day2", e.target.value)} className="w-24" />
                    </div>
                  </div>
                )}

                {form.billing_cycle_type === "weekly" && (
                  <div className="space-y-2">
                    <Label>Billing day of the week</Label>
                    <Select value={form.billing_cutoff_day ?? "1"} onValueChange={(v) => set("billing_cutoff_day", v)}>
                      <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_WEEK.map((d, i) => (
                          <SelectItem key={d} value={String(i + 1)}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  Note: Billing cycle is set by Accounting. Contact accounting before selecting.
                </p>
              </div>
            )}
          </div>

          <Separator />

          {/* Public Works / Davis-Bacon */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Compliance</h3>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox
                checked={form.is_public_works ?? false}
                onCheckedChange={(v) => set("is_public_works", !!v)}
              />
              Public works project (Davis-Bacon Act — requires certified payroll)
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={updateProject.isPending}>
              {updateProject.isPending ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
